// ── BURN RATE — Projection & Simulation engine ──────────────────────────────
// Pure calculation layer. Given saved projects and a revenue goal, generates
// scenarios, break-even analysis, and "what-if" simulations.

import type { SavedProject } from "./burn-rate-store";
import type { CalcMode } from "./burn-rate";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProjectSummary {
  id: string;
  name: string;
  mode: CalcMode;
  netPrice: number;
  baseCost: number;
  profit: number;
  frequency: number; // how many times this project was saved
}

export interface BreakEvenResult {
  monthlyFixed: number;
  requiredRevenue: number;
  byProject: Array<{
    projectId: string;
    name: string;
    unitsNeeded: number;
    revenueAtUnits: number;
  }>;
}

export interface ScenarioResult {
  id: string;
  label: string;
  description: string;
  totalRevenue: number;
  totalProfit: number;
  totalCost: number;
  items: Array<{
    name: string;
    units: number;
    unitPrice: number;
    subtotal: number;
  }>;
  reachesGoal: boolean;
  gapToGoal: number;
}

export interface ProjectionResult {
  goal: number;
  starProduct: ProjectSummary | null;
  breakEven: BreakEvenResult;
  scenarios: ScenarioResult[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getSnapshotValue(snap: Record<string, unknown>, key: string): number {
  const v = snap[key];
  if (typeof v === "string") return parseFloat(v) || 0;
  if (typeof v === "number") return v;
  return 0;
}

function computeNetPriceFromSnapshot(snap: Record<string, unknown>, mode: CalcMode): number {
  if (mode === "services") {
    const totalMonthly = ((snap.expenses as Array<{amount: string}> | undefined) ?? [])
      .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const desiredSalary = getSnapshotValue(snap, "desiredSalary");
    const hoursPerDay = getSnapshotValue(snap, "hoursPerDay") || 8;
    const daysPerMonth = getSnapshotValue(snap, "daysPerMonth") || 22;
    const billablePct = getSnapshotValue(snap, "billablePct") || 70;
    const margin = getSnapshotValue(snap, "margin") || 35;

    const totalHours = hoursPerDay * daysPerMonth;
    const billableHours = totalHours * (billablePct / 100);
    const monthlyTarget = totalMonthly + desiredSalary;
    const costPerHour = billableHours > 0 ? monthlyTarget / billableHours : 0;
    const netPrice = margin < 100 ? costPerHour / (1 - margin / 100) : 0;
    return netPrice;
  }

  // recipes / product
  const items = (snap.items as Array<{unitCost: string; quantity: string}> | undefined) ?? [];
  const rawCost = items.reduce((s, i) => s + (parseFloat(i.unitCost) || 0) * (parseFloat(i.quantity) || 0), 0);
  const mermaPct = getSnapshotValue(snap, "mermaPct");
  const rawEffective = rawCost * (1 + mermaPct / 100);
  const laborHours = getSnapshotValue(snap, "laborHours");
  const laborRate = getSnapshotValue(snap, "laborRate");
  const laborCost = laborHours * laborRate;
  const unitsPerBatch = getSnapshotValue(snap, "unitsPerBatch") || 1;
  const batchCost = rawEffective + laborCost;
  const costPerUnitRaw = unitsPerBatch > 0 ? batchCost / unitsPerBatch : 0;
  const packaging = getSnapshotValue(snap, "packagingPerUnit");
  const shipping = getSnapshotValue(snap, "shippingPerUnit");
  const equipmentCost = getSnapshotValue(snap, "equipmentCost");
  const equipmentUnits = getSnapshotValue(snap, "equipmentUnits");
  const depr = equipmentUnits > 0 ? equipmentCost / equipmentUnits : 0;
  const direct = costPerUnitRaw + packaging + shipping + depr;
  const overheadPct = getSnapshotValue(snap, "overheadPct") || 15;
  const overhead = direct * (overheadPct / 100);
  const totalCostUnit = direct + overhead;
  const margin = getSnapshotValue(snap, "margin") || 35;
  const netPrice = margin < 100 ? totalCostUnit / (1 - margin / 100) : 0;
  return netPrice;
}

function computeBaseCostFromSnapshot(snap: Record<string, unknown>, mode: CalcMode): number {
  if (mode === "services") {
    const expenses = ((snap.expenses as Array<{amount: string}> | undefined) ?? []);
    const totalMonthly = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const desiredSalary = getSnapshotValue(snap, "desiredSalary");
    const hoursPerDay = getSnapshotValue(snap, "hoursPerDay") || 8;
    const daysPerMonth = getSnapshotValue(snap, "daysPerMonth") || 22;
    const billablePct = getSnapshotValue(snap, "billablePct") || 70;
    const totalHours = hoursPerDay * daysPerMonth;
    const billableHours = totalHours * (billablePct / 100);
    const monthlyTarget = totalMonthly + desiredSalary;
    return billableHours > 0 ? monthlyTarget / billableHours : 0;
  }

  const items = (snap.items as Array<{unitCost: string; quantity: string}> | undefined) ?? [];
  const rawCost = items.reduce((s, i) => s + (parseFloat(i.unitCost) || 0) * (parseFloat(i.quantity) || 0), 0);
  const mermaPct = getSnapshotValue(snap, "mermaPct");
  const rawEffective = rawCost * (1 + mermaPct / 100);
  const laborHours = getSnapshotValue(snap, "laborHours");
  const laborRate = getSnapshotValue(snap, "laborRate");
  const laborCost = laborHours * laborRate;
  const unitsPerBatch = getSnapshotValue(snap, "unitsPerBatch") || 1;
  const batchCost = rawEffective + laborCost;
  const costPerUnitRaw = unitsPerBatch > 0 ? batchCost / unitsPerBatch : 0;
  const packaging = getSnapshotValue(snap, "packagingPerUnit");
  const shipping = getSnapshotValue(snap, "shippingPerUnit");
  const equipmentCost = getSnapshotValue(snap, "equipmentCost");
  const equipmentUnits = getSnapshotValue(snap, "equipmentUnits");
  const depr = equipmentUnits > 0 ? equipmentCost / equipmentUnits : 0;
  const direct = costPerUnitRaw + packaging + shipping + depr;
  const overheadPct = getSnapshotValue(snap, "overheadPct") || 15;
  const overhead = direct * (overheadPct / 100);
  return direct + overhead;
}

function extractFixedMonthly(snap: Record<string, unknown>): number {
  if ((snap.mode as string) === "services" || !snap.mode) {
    const expenses = ((snap.expenses as Array<{amount: string}> | undefined) ?? []);
    return expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  }
  return getSnapshotValue(snap, "fixedMonthly");
}

// ── Main projection function ─────────────────────────────────────────────────

export function runProjection(
  projects: SavedProject[],
  goal: number,
  monthlyFixed: number,
): ProjectionResult {
  // Summarize unique projects with frequency
  const freqMap = new Map<string, { proj: SavedProject; count: number }>();
  for (const p of projects) {
    const key = `${p.mode}::${p.name}`;
    const existing = freqMap.get(key);
    if (existing) {
      existing.count++;
    } else {
      freqMap.set(key, { proj: p, count: 1 });
    }
  }

  const summaries: ProjectSummary[] = Array.from(freqMap.values()).map(({ proj, count }) => {
    const netPrice = computeNetPriceFromSnapshot(proj.snapshot, proj.mode);
    const baseCost = computeBaseCostFromSnapshot(proj.snapshot, proj.mode);
    return {
      id: proj.id,
      name: proj.name,
      mode: proj.mode,
      netPrice,
      baseCost,
      profit: netPrice - baseCost,
      frequency: count,
    };
  });

  // Sort by frequency then by profit
  summaries.sort((a, b) => {
    if (b.frequency !== a.frequency) return b.frequency - a.frequency;
    return b.profit - a.profit;
  });

  const starProduct = summaries[0] ?? null;

  // Break-even
  const breakEven: BreakEvenResult = {
    monthlyFixed,
    requiredRevenue: monthlyFixed,
    byProject: summaries.map((s) => {
      const unitsNeeded = s.netPrice > 0 ? Math.ceil(monthlyFixed / s.netPrice) : 0;
      return {
        projectId: s.id,
        name: s.name,
        unitsNeeded,
        revenueAtUnits: unitsNeeded * s.netPrice,
      };
    }),
  };

  // Scenarios
  const scenarios = generateScenarios(summaries, goal, monthlyFixed, starProduct);

  return { goal, starProduct, breakEven, scenarios };
}

// ── Scenario generator ───────────────────────────────────────────────────────

function generateScenarios(
  summaries: ProjectSummary[],
  goal: number,
  monthlyFixed: number,
  starProduct: ProjectSummary | null,
): ScenarioResult[] {
  const results: ScenarioResult[] = [];
  const top = summaries.slice(0, 5);

  // 1. Solo producto estrella
  if (starProduct && starProduct.netPrice > 0) {
    const units = Math.ceil(goal / starProduct.netPrice);
    const revenue = units * starProduct.netPrice;
    const cost = units * starProduct.baseCost + monthlyFixed;
    results.push({
      id: "star-only",
      label: "Solo tu producto estrella",
      description: `Vende ${units} ${starProduct.mode === "services" ? "horas de" : "unidades de"} ${starProduct.name}`,
      totalRevenue: revenue,
      totalProfit: revenue - cost,
      totalCost: cost,
      items: [{ name: starProduct.name, units, unitPrice: starProduct.netPrice, subtotal: revenue }],
      reachesGoal: revenue >= goal,
      gapToGoal: Math.max(0, goal - revenue),
    });
  }

  // 2. Combinaciones de 2 productos (top pairs)
  for (let i = 0; i < Math.min(top.length, 3); i++) {
    for (let j = i + 1; j < Math.min(top.length, 4); j++) {
      const a = top[i];
      const b = top[j];
      if (a.netPrice <= 0 || b.netPrice <= 0) continue;

      // Find mix that reaches goal with ~reasonable units
      let best: { unitsA: number; unitsB: number; revenue: number } | null = null;
      for (let ua = 1; ua <= 50; ua++) {
        const remaining = goal - ua * a.netPrice;
        if (remaining <= 0) {
          const rev = ua * a.netPrice;
          if (!best || rev < best.revenue) best = { unitsA: ua, unitsB: 0, revenue: rev };
          break;
        }
        const ub = Math.ceil(remaining / b.netPrice);
        if (ub > 0 && ub <= 50) {
          const rev = ua * a.netPrice + ub * b.netPrice;
          if (!best || rev < best.revenue) best = { unitsA: ua, unitsB: ub, revenue: rev };
        }
      }

      if (best) {
        const items = [
          { name: a.name, units: best.unitsA, unitPrice: a.netPrice, subtotal: best.unitsA * a.netPrice },
        ];
        if (best.unitsB > 0) {
          items.push({ name: b.name, units: best.unitsB, unitPrice: b.netPrice, subtotal: best.unitsB * b.netPrice });
        }
        const cost = best.unitsA * a.baseCost + best.unitsB * b.baseCost + monthlyFixed;
        results.push({
          id: `mix-${i}-${j}`,
          label: `${a.name} + ${b.name}`,
          description: `Vende ${best.unitsA} ${a.mode === "services" ? "h" : "u"} de ${a.name}${best.unitsB > 0 ? ` + ${best.unitsB} de ${b.name}` : ""}`,
          totalRevenue: best.revenue,
          totalProfit: best.revenue - cost,
          totalCost: cost,
          items,
          reachesGoal: best.revenue >= goal,
          gapToGoal: Math.max(0, goal - best.revenue),
        });
      }
    }
  }

  // 3. Diversificado (todos los top products en proporción)
  if (top.length >= 2) {
    const totalPrice = top.reduce((s, p) => s + p.netPrice, 0);
    if (totalPrice > 0) {
      const shares = top.map((p) => ({ ...p, share: p.netPrice / totalPrice }));
      // Find smallest multiplier so that sum >= goal
      let multiplier = 1;
      while (multiplier <= 100) {
        const revenue = shares.reduce((s, sh) => s + Math.ceil(sh.share * multiplier) * sh.netPrice, 0);
        if (revenue >= goal) break;
        multiplier++;
      }
      if (multiplier <= 100) {
        const items = shares.map((sh) => {
          const units = Math.ceil(sh.share * multiplier);
          return { name: sh.name, units, unitPrice: sh.netPrice, subtotal: units * sh.netPrice };
        });
        const revenue = items.reduce((s, it) => s + it.subtotal, 0);
        const cost = top.reduce((s, p, idx) => s + items[idx].units * p.baseCost, 0) + monthlyFixed;
        results.push({
          id: "diversified",
          label: "Portafolio diversificado",
          description: `Distribuye tu esfuerzo entre tus ${top.length} productos principales`,
          totalRevenue: revenue,
          totalProfit: revenue - cost,
          totalCost: cost,
          items,
          reachesGoal: revenue >= goal,
          gapToGoal: Math.max(0, goal - revenue),
        });
      }
    }
  }

  // Sort: reached goal first, then by profit desc
  results.sort((a, b) => {
    if (a.reachesGoal !== b.reachesGoal) return a.reachesGoal ? -1 : 1;
    return b.totalProfit - a.totalProfit;
  });

  return results;
}

// ── What-if simulator ────────────────────────────────────────────────────────

export interface WhatIfParams {
  projectId: string;
  currentPrice: number;
  currentCost: number;
  newPrice?: number;
  newCost?: number;
  unitsSold: number;
}

export interface WhatIfResult {
  currentProfit: number;
  newProfit: number;
  deltaProfit: number;
  currentMargin: number;
  newMargin: number;
  breakEvenUnits: number;
}

export function runWhatIf(params: WhatIfParams): WhatIfResult {
  const { currentPrice, currentCost, newPrice, newCost, unitsSold } = params;
  const price = newPrice ?? currentPrice;
  const cost = newCost ?? currentCost;

  const currentProfit = unitsSold * (currentPrice - currentCost);
  const newProfit = unitsSold * (price - cost);
  const deltaProfit = newProfit - currentProfit;

  const currentMargin = currentPrice > 0 ? ((currentPrice - currentCost) / currentPrice) * 100 : 0;
  const newMargin = price > 0 ? ((price - cost) / price) * 100 : 0;

  const breakEvenUnits = price > cost ? Math.ceil(currentCost / (price - cost)) : 0;

  return { currentProfit, newProfit, deltaProfit, currentMargin, newMargin, breakEvenUnits };
}
